import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { MmpConfirmPopupComponent } from "../mmp-confirm-popup/mmp-confirm-popup.component";
import { MatDialog } from "@angular/material/dialog";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { ExtractionService } from "src/app/shared/services/extraction.service";
import { Observable, Subject, takeUntil } from "rxjs";
import { AppConfigService } from "src/app/services/app-config.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  FormArray,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { CommonModule } from "@angular/common";
import { MmpAiSummaryComponent } from "../mmp-ai-summary/mmp-ai-summary.component";
import { MmpFlagComponentComponent } from "../mmp-flag-component/mmp-flag-component.component";
import { LowConfidenceTagComponent } from "src/app/shared/components/low-confidence-tag/low-confidence-tag.component";
import { EditedFieldTagComponent } from "src/app/shared/components/edited-field-tag/edited-field-tag.component";
import { SalesforceTagComponent } from "src/app/shared/components/salesforce-tag/salesforce-tag.component";
import { MmpFeedbackComponent } from "../mmp-feedback/mmp-feedback.component";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";

interface StudentInformation {
  name?: string;
  date_of_birth?: string | null;
}

interface ParsedExtractionTranscript {
  session_key?: string;
  result?: {
    student_information?: StudentInformation;
  };
  college?: {
    transcript_version?: string | null;
  };
}

interface ExtractionStreamTranscript {
  status?: string;
  sessionKey?: string;
  session_key?: string;
  output?: string;
}

@Component({
  selector: "app-mmp-extraction",
  templateUrl: "./mmp-extraction.component.html",
  styleUrls: ["./mmp-extraction.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTabsModule,
    MatTooltipModule,
    MmpAiSummaryComponent,
    MmpFlagComponentComponent,
    LowConfidenceTagComponent,
    EditedFieldTagComponent,
    SalesforceTagComponent,
    MmpFeedbackComponent,
  ],
})
export class MmpExtractionComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  currentStage: string = "Extraction";
  progressTagMsg: string = "In progress";
  progressTimeText: string = "Extracting...";
  progressTime!: string;
  aiSummaryContent!: string;
  aiSummaryCourseCount!: number;
  aiSummaryFlaggedCount!: number;
  aiSummaryConfidenceScore!: number;
  extractionSummary: any;

  extractionResult: any;
  confidenceResult: any;

  isStudentEdit: boolean = false;
  collegeEditIndex: number | null = null;
  courseEditIndexMap: { [tabIndex: number]: number | null } = {};
  isCourseAddVisible: boolean = false;
  selectedTabIndex: number = 0;
  transcriptAlert: boolean = false;
  activeEdit = {
    section: null,
    transcriptIndex: null,
    courseIndex: null,
  };

  hasLowInTab: boolean[] = [];
  isHighlightToggleChecked: boolean = false;

  originalData: any = {};
  savedChanges: any = {
    student: {},
    college: {},
    course: {},
  };
  savedConfidenceChanges: any = {
    student: {},
    college: {},
    course: {},
  };
  extractionCopy: any = {};
  transcripts$!: Observable<any>;
  showConsoleLogs: boolean = false;
  maxDate = new Date();
  invalidDate: boolean = false;
  salesFlagTitle: string = "Data sourced from Salesforce";
  studentForm!: FormGroup;
  initialStudentValue: any;
  collegeForms!: FormArray;
  initialCollegeValue: any;
  creditSystemOptions = {
    quarter: "quarter",
    semester: "semester",
  };
  courseForms!: FormArray;
  initialCourseValue: any;
  newCourseForm!: FormGroup;
  extractionCompletedFlag: boolean = false;
  totalTranscriptCount: number = 0;
  private readonly transcriptSequenceBySessionKey = new Map<string, number>();
  ComparisonNormalizer = ComparisonNormalizer;

  @Output() extractionComplete = new EventEmitter<void>();
  @Input() selectedTranscriptIndex!: number;
  @Output() selectedTranscriptIndexChange = new EventEmitter<number>();
  @Output() disableTabs = new EventEmitter<string>();
  @Output() editLockChange = new EventEmitter<boolean>();
  @Output() inactiveTranscriptsIndexesChange = new EventEmitter<number[]>();

  constructor(
    private readonly confirmPopup: MatDialog,
    private readonly extractionService: ExtractionService,
    private readonly envService: AppConfigService,
    private readonly snackBar: MatSnackBar,
    private readonly fb: FormBuilder,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.collegeForms = this.fb.array([]);
    this.courseForms = this.fb.array([]);
  }

  ngOnInit(): void {
    this.extractionService.transcripts$
      .pipe(takeUntil(this.destroy$))
      .subscribe((transcripts) => {
        this.captureTranscriptSequence(transcripts);
        const isMultiTranscriptCase =
          this.isMultiTranscriptFromStream(transcripts);

        // Filter only completed files
        const completed = transcripts.filter(
          (t) => ComparisonNormalizer.string(t.status) === "completed",
        );

        const parsedTranscripts = completed.map((t) => {
          let parsed: any = {};
          try {
            parsed = JSON.parse(t.output);
          } catch (e) {
            if (this.showConsoleLogs) {
              console.log("Error parsing transcript output", e);
            }
            parsed = t.output;
          }

          const college = parsed.result.college || {};
          college.credit_system = parsed.result.credit_system;

          // Ensure student_information exists for each transcript
          return {
            courses: parsed.result.courses,
            college,
            ...parsed,
          };
        });

        const parsedConfidenceScore = completed.map((t) => {
          let parsed: any = {};
          try {
            parsed = JSON.parse(t.output);
          } catch (e) {
            if (this.showConsoleLogs) {
              console.log("Error parsing transcript output", e);
            }
            parsed = t.output;
          }

          const college = parsed.result.confidence_scores.college || {};
          college.credit_system = parsed.result.confidence_scores.credit_system;

          return {
            courses: parsed.result.confidence_scores.courses,
            college,
          };
        });

        const studentInfo = this.getStudentInformationForCurrentState(
          parsedTranscripts,
          isMultiTranscriptCase,
        );

        //check if student info is already there
        const existingStudentInfo =
          this.extractionService.getStudentInformations?.();
        if (
          !existingStudentInfo ||
          Object.keys(existingStudentInfo).length === 0
        ) {
          this.extractionService.setStudentInformations(studentInfo);
        }

        // Construct final extractionResult
        this.extractionResult = {
          student_information: studentInfo,
          transcripts: parsedTranscripts,
          confidence_scores: {
            student_information:
              parsedTranscripts[0]?.result?.confidence_scores
                ?.student_information,
            transcripts: parsedConfidenceScore,
          },
        };
        if (this.showConsoleLogs) {
          console.log("extractionResult:", this.extractionResult);
        }
        this.initialDataHandle();

        this.initStudentForm();
        this.initCollegeForms();
        this.initCourseForms();
      });
    this.extractionService.extractedSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.progressTagMsg = "Completed";
          this.extractionSummary = data.ai_summary;
          this.progressTime = this.extractionSummary.total_processing_time;
          this.aiSummaryContent = this.extractionSummary.summary;
          this.aiSummaryCourseCount = this.extractionSummary.total_courses;
          this.aiSummaryFlaggedCount =
            this.extractionSummary.flagged_items_count;
          this.aiSummaryConfidenceScore =
            this.extractionSummary.ai_summary_confidence_score;
          this.extractionCompletedFlag = true;
          this.applyDeferredFieldsAfterCompletion();
          // Ensure that transcript_version is set in extractionCopy if missing
          const isExtractionCopyVersionKeyMissing =
            this.extractionCopy.transcripts.some((transcript, index) => {
              return !transcript.college.transcript_version;
            });

          if (isExtractionCopyVersionKeyMissing) {
            this.extractionCopy.transcripts.forEach((transcript, index) => {
              transcript.college.transcript_version = structuredClone(
                this.extractionResult.transcripts[index].college
                  .transcript_version,
              );
            });
            // Ensure that date_of_birth is set in extractionCopy if missing
            this.extractionCopy.student_information.date_of_birth =
              structuredClone(
                this.extractionResult.student_information.date_of_birth,
              );
            this.extractionService.setExtractionCopy(this.extractionCopy);
          }
          this.progressTimeText = "Processing Time:";
        }
      });
  }

  private captureTranscriptSequence(
    transcripts: ExtractionStreamTranscript[],
  ): void {
    this.totalTranscriptCount = Math.max(
      this.totalTranscriptCount,
      transcripts.length,
    );

    transcripts.forEach((transcript) => {
      const sessionKey = ComparisonNormalizer.string(
        transcript?.sessionKey ?? transcript?.session_key,
      );

      if (
        sessionKey !== "" &&
        !this.transcriptSequenceBySessionKey.has(sessionKey)
      ) {
        this.transcriptSequenceBySessionKey.set(
          sessionKey,
          this.transcriptSequenceBySessionKey.size,
        );
      }
    });
  }

  isMultiTranscriptCase(): boolean {
    if (this.totalTranscriptCount > 1) {
      return true;
    }

    return (this.extractionResult?.transcripts?.length || 0) > 1;
  }

  private isMultiTranscriptFromStream(
    transcripts: ExtractionStreamTranscript[],
  ): boolean {
    if (this.totalTranscriptCount > 1) {
      return true;
    }

    return transcripts.length > 1;
  }

  private shouldDeferSensitiveFieldAssignment(
    isMultiTranscriptCase: boolean,
  ): boolean {
    return isMultiTranscriptCase && !this.extractionCompletedFlag;
  }

  shouldRenderDobField(): boolean {
    return !this.isMultiTranscriptCase() || this.extractionCompletedFlag;
  }

  shouldRenderTranscriptVersionField(): boolean {
    return this.isMultiTranscriptCase() && this.extractionCompletedFlag;
  }

  private getStudentInformationForCurrentState(
    parsedTranscripts: ParsedExtractionTranscript[],
    isMultiTranscriptCase: boolean,
  ): StudentInformation {
    const firstStudentInfo =
      parsedTranscripts[0]?.result?.student_information ?? {};

    const studentInfo: StudentInformation = {
      ...firstStudentInfo,
    };

    if (this.shouldDeferSensitiveFieldAssignment(isMultiTranscriptCase)) {
      studentInfo.date_of_birth = "";
      return studentInfo;
    }

    studentInfo.date_of_birth =
      this.resolveFinalDobByBackendSequence(parsedTranscripts);
    return studentInfo;
  }

  private resolveFinalDobByBackendSequence(
    transcripts: ParsedExtractionTranscript[],
  ): string {
    if (!Array.isArray(transcripts) || transcripts.length === 0) {
      return "";
    }

    const orderedTranscripts =
      this.orderTranscriptsByBackendSequence(transcripts);

    for (const transcript of orderedTranscripts) {
      const dob = ComparisonNormalizer.string(
        transcript?.result?.student_information?.date_of_birth,
      );

      if (dob !== "") {
        return dob;
      }
    }

    return "";
  }

  private orderTranscriptsByBackendSequence(
    transcripts: ParsedExtractionTranscript[],
  ): ParsedExtractionTranscript[] {
    return transcripts
      .map((transcript, index) => ({ transcript, index }))
      .sort((a, b) => {
        const left = this.getTranscriptSequence(a.transcript, a.index);
        const right = this.getTranscriptSequence(b.transcript, b.index);
        return left - right;
      })
      .map((item) => item.transcript);
  }

  private getTranscriptSequence(
    transcript: ParsedExtractionTranscript,
    fallbackIndex: number,
  ): number {
    const sessionKey = ComparisonNormalizer.string(transcript?.session_key);

    if (
      sessionKey !== "" &&
      this.transcriptSequenceBySessionKey.has(sessionKey)
    ) {
      return (
        this.transcriptSequenceBySessionKey.get(sessionKey) ?? fallbackIndex
      );
    }

    return fallbackIndex;
  }

  private applyDeferredFieldsAfterCompletion(): void {
    if (!this.isMultiTranscriptCase()) {
      return;
    }

    if (!this.extractionResult?.transcripts) {
      return;
    }

    const resolvedDob = this.resolveFinalDobByBackendSequence(
      this.extractionResult.transcripts,
    );

    this.extractionResult.student_information =
      this.extractionResult.student_information || {};
    this.extractionResult.student_information.date_of_birth = resolvedDob;

    if (this.studentForm) {
      this.studentForm.patchValue(
        { date_of_birth: resolvedDob },
        { emitEvent: false },
      );
      this.initialStudentValue = this.studentForm.getRawValue();
    }

    this.extractionResult.transcripts.forEach((transcript, index) => {
      const transcriptVersion =
        transcript?.college?.transcript_version || "non_duplicate";

      transcript.college = transcript.college || {};
      transcript.college.transcript_version = transcriptVersion;

      const transcriptVersionControl =
        this.getCollegeForm(index)?.get("transcript_version");
      if (transcriptVersionControl) {
        transcriptVersionControl.setValue(transcriptVersion, {
          emitEvent: false,
        });
      }
    });

    if (this.collegeForms) {
      this.initialCollegeValue = this.collegeForms.getRawValue();
    }

    this.extractionService.setStudentInformations(
      this.extractionResult.student_information,
    );
  }

  private initStudentForm(): void {
    this.studentForm = this.fb.group({
      name: [
        this.extractionResult?.student_information?.name || "",
        [
          Validators.required,
          this.noEmptySpaceValidator,
          Validators.minLength(3),
          Validators.maxLength(50),
        ],
      ],
      date_of_birth: [
        this.extractionResult?.student_information?.date_of_birth || "",
      ],
    });

    this.initialStudentValue = this.studentForm.getRawValue();
  }

  private initCollegeForms(): void {
    // Guard: nothing to init if transcripts are not ready
    if (!this.extractionResult?.transcripts) {
      return;
    }

    // Ensure FormArray exists
    if (this.collegeForms) {
      this.collegeForms.clear();
    } else {
      this.collegeForms = this.fb.array([]);
    }

    this.extractionResult.transcripts.forEach((t) => {
      const college = t.college || {};
      const accreditation = college.college_accreditation || {};
      const transcriptVersionValue = this.shouldDeferSensitiveFieldAssignment(
        this.isMultiTranscriptCase(),
      )
        ? null
        : college.transcript_version || "non_duplicate";

      this.collegeForms.push(
        this.fb.group({
          institution_name: [
            college.institution_name || "",
            [
              Validators.required,
              this.noEmptySpaceValidator,
              Validators.maxLength(555),
            ],
          ],

          credit_system: [college.credit_system || null, [Validators.required]],

          transcript_issue_date: [college.transcript_issue_date || ""],

          is_accredited: [
            accreditation.is_accredited ?? null,
            [Validators.required],
          ],

          transcript_version: [transcriptVersionValue, [Validators.required]],
        }),
      );
    });

    this.initialCollegeValue = this.collegeForms.getRawValue();
  }

  getCollegeForm(i: number): FormGroup {
    // Guard in case it's called before init
    if (!this.collegeForms || this.collegeForms.length <= i) {
      // Return an empty group rather than throwing
      return this.fb.group({});
    }

    return this.collegeForms.at(i) as FormGroup;
  }

  private initCourseForms(): void {
    if (!this.extractionResult?.transcripts) {
      return;
    }

    // Ensure FormArray exists
    if (this.courseForms) {
      this.courseForms.clear();
    } else {
      this.courseForms = this.fb.array([]);
    }

    this.extractionResult.transcripts.forEach((t) => {
      const courses = t.courses || [];
      const courseArray = this.fb.array(
        courses.map((course: any) =>
          this.fb.group(
            {
              course_code: [
                course.course_code || "",
                [
                  Validators.required,
                  this.noEmptySpaceValidator,
                  Validators.maxLength(50),
                ],
              ],
              course_name: [
                course.course_name || "",
                [
                  Validators.required,
                  this.noEmptySpaceValidator,
                  Validators.maxLength(555),
                ],
              ],
              semester: [
                course.semester || "",
                [
                  Validators.required,
                  this.noEmptySpaceValidator,
                  Validators.maxLength(50),
                ],
              ],
              credits_attempted: [
                course.credits_attempted ?? null,
                [Validators.required, Validators.min(0), Validators.max(10)],
              ],
              credits_earned: [
                course.credits_earned ?? null,
                [Validators.required, Validators.min(0), Validators.max(10)],
              ],
              grade: [
                course.grade || "",
                [
                  Validators.required,
                  this.noEmptySpaceValidator,
                  Validators.maxLength(10),
                ],
              ],
            },
            {
              validators: this.creditsEarnedNotExceedAttemptedValidator,
            },
          ),
        ),
      );

      this.courseForms.push(courseArray);
    });

    this.initialCourseValue = this.courseForms.getRawValue();
  }

  private initNewCourseForm(): void {
    this.newCourseForm = this.fb.group(
      {
        course_code: [
          "",
          [
            Validators.required,
            this.noEmptySpaceValidator,
            Validators.maxLength(50),
          ],
        ],
        course_name: [
          "",
          [
            Validators.required,
            this.noEmptySpaceValidator,
            Validators.maxLength(555),
          ],
        ],
        semester: [
          "",
          [
            Validators.required,
            this.noEmptySpaceValidator,
            Validators.maxLength(50),
          ],
        ],
        credits_attempted: [
          null,
          [Validators.required, Validators.min(0), Validators.max(10)],
        ],
        credits_earned: [
          null,
          [Validators.required, Validators.min(0), Validators.max(10)],
        ],
        grade: [
          "",
          [
            Validators.required,
            this.noEmptySpaceValidator,
            Validators.maxLength(10),
          ],
        ],
      },
      { validators: this.creditsEarnedNotExceedAttemptedValidator },
    );
  }

  private readonly creditsEarnedNotExceedAttemptedValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const attemptedRaw = group.get("credits_attempted")?.value;
    const earnedRaw = group.get("credits_earned")?.value;

    if (attemptedRaw == null || earnedRaw == null) {
      return null;
    }

    const attempted = Number(attemptedRaw);
    const earned = Number(earnedRaw);

    if (Number.isNaN(attempted) || Number.isNaN(earned)) {
      return null;
    }

    return earned > attempted ? { creditsMismatch: true } : null;
  };

  private readonly noEmptySpaceValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined) {
      return null;
    }
    const isWhitespaceOnly =
      typeof value === "string" && value.trim().length === 0;
    return isWhitespaceOnly ? { whitespace: true } : null;
  };

  initialDataHandle() {
    this.extractionService.setExtractionData(this.extractionResult);
    const isExtractionCopyEmpty = this.checkExtractionCopyAvailable();
    if (isExtractionCopyEmpty) {
      this.extractionService.setExtractionCopy(this.extractionResult);
    }

    // Get existing confidence data from the service
    const existingConfidence = this.extractionService.getConfidenceData?.();
    const isConfidenceEmpty =
      !existingConfidence ||
      (typeof existingConfidence === "object" &&
        Object.keys(existingConfidence).length === 0);

    //check transcripts count in confidence data and extraction result to avoid mismatch
    const confidenceTranscriptCount = Array.isArray(
      existingConfidence?.transcripts,
    )
      ? existingConfidence.transcripts.length
      : 0;
    const extractionTranscriptCount =
      this.extractionResult?.transcripts?.length || 0;

    // Only set confidence data from extractionResult if there is no existing data
    if (
      isConfidenceEmpty ||
      ComparisonNormalizer.number(confidenceTranscriptCount) !==
        ComparisonNormalizer.number(extractionTranscriptCount)
    ) {
      this.extractionService.setConfidenceData(
        this.extractionResult.confidence_scores,
      );
    }

    this.confidenceResult = structuredClone(
      this.extractionService.getConfidenceData(),
    );

    if (this.showConsoleLogs) {
      console.log("confidenceResult:", this.confidenceResult);
    }
    //extraction copy to implement cancel.
    this.originalData = structuredClone(this.extractionResult);
    //extraction copy without any changes - Edit hover
    this.extractionCopy = structuredClone(
      this.extractionService.getExtractionCopy(),
    );
    this.savedChanges = structuredClone(
      this.extractionService.getEditChanges(),
    );

    this.savedConfidenceChanges = structuredClone(
      this.extractionService.getConfidenceChanges(),
    );
    if (this.showConsoleLogs) {
      console.log("savedConfidenceChanges:", this.savedConfidenceChanges);
    }

    this.hasLowInTab = this.extractionResult.transcripts.map((t, i) =>
      this.hasLowConfidenceInTab(i),
    );
  }

  checkExtractionCopyAvailable() {
    /*** extractionCopy: initialize in service if it's empty or if the new extraction includes additional transcripts ***/
    const existingExtractionCopy = this.extractionService.getExtractionCopy?.();
    const existingExtractionCopyTranscriptCount = Array.isArray(
      existingExtractionCopy?.transcripts,
    )
      ? existingExtractionCopy.transcripts.length
      : 0;
    const extractionResultTranscriptCount = Array.isArray(
      this.extractionResult?.transcripts,
    )
      ? this.extractionResult.transcripts.length
      : 0;
    const isExtractionCopyEmpty =
      !existingExtractionCopy ||
      (typeof existingExtractionCopy === "object" &&
        Object.keys(existingExtractionCopy).length === 0) ||
      existingExtractionCopyTranscriptCount < extractionResultTranscriptCount;
    return isExtractionCopyEmpty;
  }

  isEditLocked() {
    return this.activeEdit.section !== null;
  }

  private emitEditLockState(): void {
    this.editLockChange.emit(this.isEditLocked());
  }

  isExtractionInProgress(): boolean {
    return this.extractionCompletedFlag === false;
  }

  isEditActionLocked(): boolean {
    return this.isEditLocked() || this.isExtractionInProgress();
  }

  studentInfoEdit() {
    if (this.isEditActionLocked()) return;
    this.activeEdit = {
      section: "student",
      transcriptIndex: null,
      courseIndex: null,
    };
    this.isStudentEdit = true;
    this.emitEditLockState();
  }

  saveStudentEdit(): void {
    if (!this.studentForm) {
      return;
    }

    if (this.studentForm.invalid) {
      this.studentForm.markAllAsTouched();

      const message = this.getStudentValidationMessage();
      this.showToast("toast-error", message);
      return;
    }

    const value = this.studentForm.value;

    // Write back into data model
    this.extractionResult.student_information.name = value.name;
    this.extractionResult.student_information.date_of_birth =
      value.date_of_birth;

    this.disableTabs.emit(this.currentStage.toLowerCase());

    const current = this.extractionResult.student_information;
    const original = this.originalData.student_information;
    const baselineStudent =
      this.extractionCopy?.student_information || original;

    for (const field in current) {
      const fieldChanged =
        ComparisonNormalizer.string(current[field]) !==
        ComparisonNormalizer.string(baselineStudent[field]);

      if (fieldChanged) {
        if (!Object.hasOwn(this.savedChanges.student, field)) {
          this.savedChanges.student[field] = baselineStudent[field];
        }
        this.confidenceResult.student_information[field] = 1;
        if (
          this.isLowConfidence(
            this.extractionResult.confidence_scores.student_information[field],
          )
        ) {
          this.savedConfidenceChanges.student[field] =
            this.extractionResult.confidence_scores.student_information[field];
        }
      } else {
        delete this.savedChanges.student[field];
      }
    }
    // Update original after saving
    this.originalData.student_information = structuredClone(current);
    this.isStudentEdit = false;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.updateHasLowInTab();
    this.initialStudentValue = this.studentForm.getRawValue();
  }

  private getStudentValidationMessage(): string {
    const nameControl = this.studentForm.get("name");

    if (nameControl?.hasError("required")) {
      return "Name is required.";
    }

    if (nameControl?.hasError("minlength")) {
      return "Name must be at least 3 characters long.";
    }

    if (nameControl?.hasError("maxlength")) {
      return "Name must be at most 50 characters long.";
    }

    // Fallback generic message
    return "Please correct the highlighted errors before continuing.";
  }

  cancelStudentEdit() {
    this.extractionResult.student_information = structuredClone(
      this.originalData.student_information,
    );
    this.studentForm.reset(this.initialStudentValue);
    this.isStudentEdit = false;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
  }

  editCollege(tabIndex: number) {
    if (this.isEditActionLocked()) return;
    this.collegeEditIndex = tabIndex;
    this.activeEdit = {
      section: "college",
      transcriptIndex: tabIndex,
      courseIndex: null,
    };
    this.emitEditLockState();
  }

  saveCollegeEdit(tabIndex: number) {
    const collegeForm = this.getCollegeForm(tabIndex);

    // Use reactive form validation instead of manual field checks
    if (!collegeForm) {
      return;
    }

    if (collegeForm.invalid) {
      collegeForm.markAllAsTouched();
      this.showToast(
        "toast-error",
        "All required fields must be filled before continuing",
      );
      return;
    }

    this.disableTabs.emit(this.currentStage.toLowerCase());

    const current = this.extractionResult.transcripts[tabIndex].college;
    this.applyCollegeFormValue(current, collegeForm.value);

    const original = this.originalData.transcripts[tabIndex].college;
    this.updateCollegeSavedChangesAndConfidence(tabIndex, current, original);
    this.finalizeCollegeSave(tabIndex, collegeForm, current);
  }

  private applyCollegeFormValue(current: any, formValue: any): void {
    current.institution_name = formValue.institution_name;
    current.credit_system = formValue.credit_system;
    current.transcript_issue_date = formValue.transcript_issue_date;
    current.college_accreditation = current.college_accreditation || {};
    current.college_accreditation.is_accredited = formValue.is_accredited;
    current.transcript_version = formValue.transcript_version;
  }

  private updateCollegeSavedChangesAndConfidence(
    tabIndex: number,
    current: any,
    original: any,
  ): void {
    const baselineCollege =
      this.extractionCopy?.transcripts?.[tabIndex]?.college || original;
    this.savedChanges.college[tabIndex] ??= {};
    for (const field in current) {
      if (ComparisonNormalizer.string(field) === "college_accreditation") {
        this.updateCollegeAccreditationChanges(
          tabIndex,
          field,
          current,
          baselineCollege,
        );
        continue;
      }

      const fieldChanged =
        ComparisonNormalizer.string(current[field]) !==
        ComparisonNormalizer.string(baselineCollege[field]);
      if (fieldChanged) {
        if (!Object.hasOwn(this.savedChanges.college[tabIndex], field)) {
          this.savedChanges.college[tabIndex][field] = baselineCollege[field];
        }
        this.confidenceResult.transcripts[tabIndex].college[field] = 1;
        if (
          this.isLowConfidence(
            this.extractionResult.confidence_scores.transcripts[tabIndex]
              .college[field],
          )
        ) {
          this.savedConfidenceChanges.course[tabIndex] ??= {};
          this.savedConfidenceChanges.course[tabIndex][0] ??= {};
          this.savedConfidenceChanges.course[tabIndex][0][field] =
            this.extractionResult.confidence_scores.transcripts[
              tabIndex
            ].college[field];
        }
      } else {
        delete this.savedChanges.college[tabIndex][field];
      }
    }
  }

  private updateCollegeAccreditationChanges(
    tabIndex: number,
    field: string,
    current: any,
    baselineCollege: any,
  ): void {
    const currentAccredited = ComparisonNormalizer.boolean(
      current?.college_accreditation?.is_accredited,
    );
    const baselineAccredited = ComparisonNormalizer.boolean(
      baselineCollege?.college_accreditation?.is_accredited,
    );
    const accreditationChanged = currentAccredited !== baselineAccredited;

    if (!accreditationChanged) {
      delete this.savedChanges.college[tabIndex][field];
      return;
    }

    if (!Object.hasOwn(this.savedChanges.college[tabIndex], field)) {
      this.savedChanges.college[tabIndex][field] = baselineAccredited;
    }

    this.confidenceResult.transcripts[tabIndex].college[field][
      "is_accredited"
    ] = 1;
    if ("reason" in current.college_accreditation) {
      this.confidenceResult.transcripts[tabIndex].college[field]["reason"] = 1;
    }
  }

  private finalizeCollegeSave(
    tabIndex: number,
    collegeForm: FormGroup,
    current: any,
  ): void {
    // Update original snapshot after saving
    this.originalData.transcripts[tabIndex].college = structuredClone(current);

    if (Array.isArray(this.initialCollegeValue)) {
      this.initialCollegeValue[tabIndex] = collegeForm.getRawValue();
    }

    this.collegeEditIndex = null;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.transcriptAlert = false;
    this.updateHasLowInTab();
  }

  cancelCollegeEdit(tabIndex: number) {
    this.extractionResult.transcripts[tabIndex].college = structuredClone(
      this.originalData.transcripts[tabIndex].college,
    );
    this.collegeForms.reset(this.initialCollegeValue);
    this.collegeEditIndex = null;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.transcriptAlert = false;
  }

  getCourseFormArray(transcriptIndex: number): FormArray {
    if (!this.courseForms || this.courseForms.length <= transcriptIndex) {
      return this.fb.array([]);
    }
    return this.courseForms.at(transcriptIndex) as FormArray;
  }

  getCourseForm(transcriptIndex: number, courseIndex: number): FormGroup {
    const courseArray = this.getCourseFormArray(transcriptIndex);
    if (!courseArray || courseArray.length <= courseIndex) {
      return this.fb.group({});
    }
    return courseArray.at(courseIndex) as FormGroup;
  }

  getCourseFormControl(
    transcriptIndex: number,
    courseIndex: number,
    controlName: string,
  ): AbstractControl | null {
    return this.getCourseForm(transcriptIndex, courseIndex).get(controlName);
  }

  editCourse(tabIndex: number, courseIndex: number) {
    if (this.isEditActionLocked()) return;

    this.courseEditIndexMap[tabIndex] = courseIndex;
    this.activeEdit = {
      section: "course",
      transcriptIndex: tabIndex,
      courseIndex: courseIndex,
    };
    this.emitEditLockState();
  }

  saveCourseEdit(tabIndex: number, courseIndex: number) {
    const courseForm = this.getCourseForm(tabIndex, courseIndex);

    if (!courseForm) {
      return;
    }

    if (courseForm.invalid) {
      courseForm.markAllAsTouched();
      const message = this.getCourseValidationMessage(
        tabIndex,
        courseIndex,
        "save",
      );
      this.showToast("toast-error", message);
      return;
    }

    this.disableTabs.emit(this.currentStage.toLowerCase());

    const formValue = courseForm.value;

    const current =
      this.extractionResult.transcripts[tabIndex].courses[courseIndex];
    const original =
      this.originalData.transcripts[tabIndex].courses[courseIndex];

    current.course_code = formValue.course_code;
    current.course_name = formValue.course_name;
    current.semester = formValue.semester;
    current.credits_attempted = String(formValue.credits_attempted);
    current.credits_earned = String(formValue.credits_earned);
    current.grade = formValue.grade;

    this.savedChanges.course[tabIndex] ??= {};
    this.savedChanges.course[tabIndex][courseIndex] ??= {};

    for (const field in current) {
      const baselineValue = this.getSavedCourseBaselineValue(
        tabIndex,
        courseIndex,
        field,
        original,
      );

      if (
        ComparisonNormalizer.string(current[field]) !==
        ComparisonNormalizer.string(baselineValue)
      ) {
        if (
          !Object.hasOwn(this.savedChanges.course[tabIndex][courseIndex], field)
        ) {
          this.savedChanges.course[tabIndex][courseIndex][field] =
            baselineValue;
        }

        // Mark confidence score as 1 for edited course field
        this.confidenceResult.transcripts[tabIndex].courses[courseIndex][
          field
        ] = 1;

        if (
          this.extractionResult.confidence_scores.transcripts[tabIndex].courses[
            courseIndex
          ] &&
          this.isLowConfidence(
            this.extractionResult.confidence_scores.transcripts[tabIndex]
              .courses[courseIndex][field],
          )
        ) {
          this.savedConfidenceChanges.course[tabIndex] ??= {};
          this.savedConfidenceChanges.course[tabIndex][courseIndex] ??= {};
          this.savedConfidenceChanges.course[tabIndex][courseIndex][field] =
            this.extractionResult.confidence_scores.transcripts[
              tabIndex
            ].courses[courseIndex][field];
        }
      } else {
        delete this.savedChanges.course[tabIndex][courseIndex][field];
        delete this.savedConfidenceChanges.course?.[tabIndex]?.[courseIndex]?.[
          field
        ];
      }
    }

    this.cleanupEmptyCourseChangeEntries(tabIndex, courseIndex);

    this.originalData.transcripts[tabIndex].courses[courseIndex] =
      structuredClone(current);

    if (Array.isArray(this.initialCourseValue)) {
      const transcriptCourses = this.initialCourseValue[tabIndex] || [];
      transcriptCourses[courseIndex] = courseForm.getRawValue();
      this.initialCourseValue[tabIndex] = transcriptCourses;
    }

    this.courseEditIndexMap[tabIndex] = null;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.updateHasLowInTab();
    this.transcriptAlert = false;
  }

  private getSavedCourseBaselineValue(
    tabIndex: number,
    courseIndex: number,
    field: string,
    originalCourse: any,
  ): any {
    const savedCourseChanges =
      this.savedChanges.course?.[tabIndex]?.[courseIndex];

    if (savedCourseChanges && Object.hasOwn(savedCourseChanges, field)) {
      return savedCourseChanges[field];
    }

    return originalCourse[field];
  }

  private cleanupEmptyCourseChangeEntries(
    tabIndex: number,
    courseIndex: number,
  ): void {
    const savedCourseChanges =
      this.savedChanges.course?.[tabIndex]?.[courseIndex];
    if (savedCourseChanges && Object.keys(savedCourseChanges).length === 0) {
      delete this.savedChanges.course[tabIndex][courseIndex];
    }

    const transcriptCourseChanges = this.savedChanges.course?.[tabIndex];
    if (
      transcriptCourseChanges &&
      Object.keys(transcriptCourseChanges).length === 0
    ) {
      delete this.savedChanges.course[tabIndex];
    }

    const savedConfidenceChanges =
      this.savedConfidenceChanges.course?.[tabIndex]?.[courseIndex];
    if (
      savedConfidenceChanges &&
      Object.keys(savedConfidenceChanges).length === 0
    ) {
      delete this.savedConfidenceChanges.course[tabIndex][courseIndex];
    }

    const transcriptConfidenceChanges =
      this.savedConfidenceChanges.course?.[tabIndex];
    if (
      transcriptConfidenceChanges &&
      Object.keys(transcriptConfidenceChanges).length === 0
    ) {
      delete this.savedConfidenceChanges.course[tabIndex];
    }
  }

  cancelCourseEdit(tabIndex: number, courseIndex: number) {
    // Reset model back to original
    this.extractionResult.transcripts[tabIndex].courses[courseIndex] =
      structuredClone(
        this.originalData.transcripts[tabIndex].courses[courseIndex],
      );

    // Reset the reactive form for this course
    const courseForm = this.getCourseForm(tabIndex, courseIndex);
    if (courseForm && Array.isArray(this.initialCourseValue)) {
      const transcriptCourses = this.initialCourseValue[tabIndex] || [];
      const initialCourse = transcriptCourses[courseIndex];

      if (initialCourse) {
        courseForm.reset(initialCourse);
      }
    }

    this.courseEditIndexMap[tabIndex] = null;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.transcriptAlert = false;
  }

  areCourseFieldsEmpty(transcriptIndex: number, courseIndex: number): boolean {
    const courseForm = this.getCourseForm(transcriptIndex, courseIndex);
    if (!courseForm) {
      return true;
    }

    // If the form is invalid, treat as "fields not correctly filled"
    return courseForm.invalid;
  }

  isOutOfRange(value: number, min: number = 0, max: number = 10): boolean {
    return value < min || value > max;
  }

  getCourseValidationMessage(
    transcriptIndex: number,
    courseIndex: number,
    type: "save" | "add" = "save",
  ): string {
    let courseForm;
    if (type === "save") {
      courseForm = this.getCourseForm(transcriptIndex, courseIndex);
    } else if (type === "add") {
      courseForm = this.newCourseForm;
    }
    if (!courseForm) {
      return "Please correct the highlighted errors before continuing.";
    }

    const controls = courseForm.controls;

    if (
      controls["course_code"]?.hasError("required") ||
      controls["course_code"]?.hasError("whitespace")
    ) {
      return "Course code is required.";
    }

    if (
      controls["course_name"]?.hasError("required") ||
      controls["course_name"]?.hasError("whitespace")
    ) {
      return "Course name is required.";
    }

    if (
      controls["semester"]?.hasError("required") ||
      controls["semester"]?.hasError("whitespace")
    ) {
      return "Attendance Date is required.";
    }

    if (controls["credits_attempted"]?.hasError("required")) {
      return "Credits attempted is required.";
    }

    if (
      controls["credits_attempted"]?.hasError("min") ||
      controls["credits_attempted"]?.hasError("max")
    ) {
      return "Credits attempted must be between 0 and 10.";
    }

    if (controls["credits_earned"]?.hasError("required")) {
      return "Credits earned is required.";
    }

    if (
      controls["credits_earned"]?.hasError("min") ||
      controls["credits_earned"]?.hasError("max")
    ) {
      return "Credits earned must be between 0 and 10.";
    }

    if (courseForm.hasError("creditsMismatch")) {
      return "Credits earned must be less than or equal to credits attempted.";
    }

    if (
      controls["grade"]?.hasError("required") ||
      controls["grade"]?.hasError("whitespace")
    ) {
      return "Grade is required.";
    }

    return "Please correct the highlighted errors before continuing.";
  }

  onTranscriptTabChange(index: number): void {
    if (this.isTabSwitchBlocked()) {
      this.transcriptAlert = true;
      this.selectedTranscriptIndex = this.selectedTabIndex;
    } else {
      this.transcriptAlert = false;
      this.selectedTabIndex = index;
    }
  }

  onTranscriptTabClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const isTabHeaderClick =
      target?.closest(".mat-mdc-tab, .mdc-tab, .mat-mdc-tab-label") !== null;

    if (!isTabHeaderClick) {
      return;
    }

    if (this.isTabSwitchBlocked()) {
      this.transcriptAlert = true;
      this.selectedTranscriptIndex = this.selectedTabIndex;
    } else {
      this.transcriptAlert = false;
      setTimeout(() => {
        this.selectedTranscriptIndexChange.emit(this.selectedTranscriptIndex);
      }, 10);
    }
  }

  isAnyCourseEditing(): boolean {
    if (ComparisonNormalizer.string(this.activeEdit.section) === "course") {
      return true;
    }

    return Object.values(this.courseEditIndexMap || {}).some(
      (value) => value !== null && value !== undefined,
    );
  }

  isTabSwitchBlocked(): boolean {
    return (
      this.collegeEditIndex !== null ||
      this.isAnyCourseEditing() ||
      this.isCourseAddVisible
    );
  }

  isAnyCollegeAccredited(): boolean {
    return this.extractionResult.transcripts.some(
      (t) =>
        ComparisonNormalizer.boolean(
          t.college?.college_accreditation?.is_accredited,
        ) === true,
    );
  }

  onSubmitExtraction() {
    // Collect transcripts with each type of issue
    const notAccreditedTranscripts = this.extractionResult.transcripts.filter(
      (t) =>
        ComparisonNormalizer.boolean(
          t.college?.college_accreditation?.is_accredited,
        ) === false,
    );

    const emptyCollegeNameTranscripts =
      this.extractionResult.transcripts.filter((t) => {
        const val = t.college?.institution_name;
        return (
          val === null ||
          val === undefined ||
          typeof val !== "string" ||
          val.trim() === ""
        );
      });

    const emptyCreditSystemTranscripts =
      this.extractionResult.transcripts.filter((t) => {
        const val = t.college?.credit_system;
        return val === null || val === undefined || val === "";
      });

    const duplicateOrOlderTranscripts =
      this.extractionResult.transcripts.filter((t, i) => {
        const val = this.getCollegeForm(i)?.get("transcript_version")?.value;
        this.extractionService.setTranscriptVersion(t.session_key, val);
        return val === "duplicate" || val === "older";
      });

    const hasFalse = notAccreditedTranscripts.length > 0;
    const hasEmptyCollegeName = emptyCollegeNameTranscripts.length > 0;
    const hasEmptyCreditSystem = emptyCreditSystemTranscripts.length > 0;
    const hasDuplicateOrOlder = duplicateOrOlderTranscripts.length > 0;

    const inactiveSet = new Set([
      ...notAccreditedTranscripts,
      ...emptyCollegeNameTranscripts,
      ...emptyCreditSystemTranscripts,
      ...duplicateOrOlderTranscripts,
    ]);

    this.extractionResult.transcripts.forEach((t: any) => {
      t.eligibleForProceeding = !inactiveSet.has(t);
    });

    const inactiveIndexes = this.extractionResult.transcripts
      .map((t: any, i: number) => (inactiveSet.has(t) ? i : -1))
      .filter((i: number) => i !== -1);
    this.inactiveTranscriptsIndexesChange.emit(inactiveIndexes);

    if (
      hasFalse ||
      hasEmptyCreditSystem ||
      hasEmptyCollegeName ||
      hasDuplicateOrOlder
    ) {
      const issues: { title: string; description: string }[] = [];

      if (hasFalse) {
        const names = notAccreditedTranscripts
          .map((t) => t.name || "Unknown")
          .join(", ");
        issues.push({
          title: "Not accredited transcripts",
          description: `Transcripts: ${names} - Only accredited transcripts can be evaluated.`,
        });
      }

      if (hasEmptyCreditSystem) {
        const names = emptyCreditSystemTranscripts
          .map((t) => t.name || "Unknown")
          .join(", ");
        issues.push({
          title: "Missing credit system",
          description: `Transcripts: ${names} - Only transcripts with a credit system can be evaluated.`,
        });
      }

      if (hasEmptyCollegeName) {
        const names = emptyCollegeNameTranscripts
          .map((t) => t.name || "Unknown")
          .join(", ");
        issues.push({
          title: "Missing college name",
          description: `Transcripts: ${names} - Only transcripts with a college name can be evaluated.`,
        });
      }

      if (hasDuplicateOrOlder) {
        const names = duplicateOrOlderTranscripts
          .map((t) => t.name || "Unknown")
          .join(", ");
        issues.push({
          title: "Duplicate or older transcripts",
          description: `Transcripts: ${names} - Duplicate or older version transcripts cannot be evaluated.`,
        });
      }

      issues.push({
        title: "",
        description:
          "Update these transcripts to include them in evaluation, or continue with eligible transcripts only.",
      });

      const dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
        width: "690px",
        data: {
          title: "Confirm Before Moving Forward",
          message: MESSAGES.EXTRACTION.CONTINUE_WARNING,
          issues,
          cancelText: "Review Transcripts",
          confirmText: "Proceed with Eligible",
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (ComparisonNormalizer.string(result) === "yes") {
            this.extractionCompleted();
          }
        });
    } else {
      this.extractionCompleted();
    }
  }

  extractionCompleted() {
    this.extractionService.setExtractionData(this.extractionResult);
    this.extractionService.setConfidenceData(this.confidenceResult);
    this.extractionService.setEditChanges(this.savedChanges);
    this.extractionService.setConfidenceChanges(this.savedConfidenceChanges);

    //modifiy original data
    this.extractionResult.transcripts.forEach((transcript, key) => {
      const sessionKey = transcript.session_key;
      const modifiedOutput: any = {};
      modifiedOutput.name = transcript.name;
      modifiedOutput.session_key = transcript.session_key;
      modifiedOutput.result = transcript.result;
      modifiedOutput.result.courses = transcript.courses;
      modifiedOutput.result.college = transcript.college;
      modifiedOutput.result.student_information = structuredClone(
        this.extractionResult.student_information,
      );
      modifiedOutput.result.student_information.institution_name =
        transcript.college.institution_name;
      modifiedOutput.result.student_information.transcript_issue_date =
        transcript.college.transcript_issue_date;
      modifiedOutput.result.college_accreditation.is_accredited =
        transcript.college.college_accreditation.is_accredited;
      modifiedOutput.result.credit_system = transcript.college.credit_system;
      modifiedOutput.result.transcript_version =
        transcript.college.transcript_version;
      modifiedOutput.result.confidence_scores.courses =
        this.confidenceResult.transcripts[key].courses;
      modifiedOutput.result.confidence_scores.college =
        this.confidenceResult.transcripts[key].college;
      modifiedOutput.result.confidence_scores.credit_system =
        this.confidenceResult.transcripts[key].college?.credit_system;

      modifiedOutput.result.frontend_data = {
        savedChanges: {
          student: this.savedChanges.student,
          college: this.savedChanges.college[key] || {},
          course: this.savedChanges.course[key] || {},
        },
        savedConfidenceChanges: {
          student: this.savedConfidenceChanges.student,
          college: this.savedConfidenceChanges.college[key] || {},
          course: this.savedConfidenceChanges.course[key] || {},
        },
      };

      modifiedOutput.result.frontend_data.eligibleForProceeding =
        transcript.eligibleForProceeding;
      this.extractionService.updateTranscriptOutput(
        sessionKey,
        modifiedOutput,
        transcript.eligibleForProceeding,
      );
    });

    this.extractionComplete.emit();
  }

  formatDatePreservingInput(dateValue) {
    if (!dateValue) return dateValue;

    if (typeof dateValue === "string") return dateValue;

    if (typeof dateValue.format === "function") {
      const input = dateValue._i; // original input
      const hasYear = /^\d{2}\/\d{2}\/\d{4}$/.test(input);

      return dateValue.format(hasYear ? "MM/DD/YYYY" : "MM/DD");
    }

    return dateValue;
  }

  handleSaveAndProceedClick(event: Event): void {
    if (this.isExtractionInProgress()) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "Extraction is still in progress. Please wait for completion before proceeding.",
      );
    } else if (!this.isAnyCollegeAccredited()) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "We couldn't verify the accreditation status of this college. Please check and update the status to 'Accredited' to continue with the evaluation process.",
      );
    } else if (this.areAllCreditSystemsEmpty()) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "Credit Type is required. Please update it to continue the evaluation process",
      );
    } else if (!this.isAnyCollegeHasName()) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "College Name is required. Please update it to continue the evaluation process",
      );
      //touch the college form controls to show validation errors
      this.collegeForms.controls.forEach((collegeForm) => {
        collegeForm.get("institution_name")?.markAsTouched();
      });
    } else if (this.hasDuplicateOrOlderTranscriptVersion()) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "All transcripts are marked as duplicate or older versions. Please review transcript version before proceeding.",
      );
    } else if (this.isEditLocked()) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "Please save or cancel the edited section to proceed.",
      );
    } else {
      this.onSubmitExtraction();
    }
  }

  isLowConfidence(confidenceScore: number | null | undefined): boolean {
    if (typeof confidenceScore !== "number" || Number.isNaN(confidenceScore)) {
      return false;
    }

    return confidenceScore < 0.75;
  }

  hasLowConfidenceInTab(tabIndex: number): boolean {
    const transcript = this.confidenceResult.transcripts?.[tabIndex];
    if (!transcript) {
      return false;
    }

    //check for low confidence in student information
    if (this.hasLowConfidenceInStudent()) {
      return true;
    }

    // Check for low-confidence in college information
    if (
      this.isLowConfidence(transcript?.college?.institution_name) ||
      this.isLowConfidence(transcript?.college?.transcript_issue_date) ||
      this.isLowConfidence(transcript?.college?.credit_system) ||
      this.isLowConfidence(
        transcript?.college?.college_accreditation?.is_accredited,
      )
    ) {
      return true;
    }

    // Check for low-confidence in courses (only if courses exist)
    if (Array.isArray(transcript.courses) && transcript.courses.length > 0) {
      for (const course of transcript.courses) {
        if (
          this.isLowConfidence(course.course_code) ||
          this.isLowConfidence(course.course_name) ||
          this.isLowConfidence(course.semester) ||
          this.isLowConfidence(course.credits_attempted) ||
          this.isLowConfidence(course.credits_earned) ||
          this.isLowConfidence(course.grade)
        ) {
          return true;
        }
      }
    }

    return false;
  }

  updateHasLowInTab(): void {
    this.hasLowInTab = this.extractionResult.transcripts.map((t, i) =>
      this.hasLowConfidenceInTab(i),
    );
  }

  markStudentInfoAsCorrect(field: string) {
    this.confidenceResult.student_information[field] = 1;
    this.updateHasLowInTab();
  }

  markCollegeFieldAsCorrect(index: number, field: string) {
    if (ComparisonNormalizer.string(field) === "college_accreditation") {
      this.confidenceResult.transcripts[index].college[field]["is_accredited"] =
        1;
    } else {
      this.confidenceResult.transcripts[index].college[field] = 1;
    }
    this.updateHasLowInTab();
  }

  markCourseFieldAsCorrect(
    transcriptIndex: number,
    courseIndex: number,
    field: string,
  ) {
    this.confidenceResult.transcripts[transcriptIndex].courses[courseIndex][
      field
    ] = 1;
    this.updateHasLowInTab(); // Or a specific updateHasLowInCourse() if you track course-level low confidence
  }

  hasLowConfidenceInStudent(): boolean {
    const student = this.confidenceResult.student_information;
    if (!student) return false;

    return (
      this.isLowConfidence(student.name) ||
      this.isLowConfidence(student.date_of_birth)
      // add more student fields if needed
    );
  }

  hasLowConfidenceInCollege(tabIndex: number): boolean {
    const college = this.confidenceResult.transcripts[tabIndex]?.college;
    if (!college) return false;

    return (
      this.isLowConfidence(college.institution_name) ||
      this.isLowConfidence(college.transcript_issue_date) ||
      this.isLowConfidence(college.credit_system) ||
      this.isLowConfidence(college.college_accreditation.is_accredited)
    );
  }
  hasLowConfidenceInCourse(tabIndex: number, courseIndex: number): boolean {
    const course =
      this.confidenceResult.transcripts[tabIndex]?.courses[courseIndex];
    if (!course) return false;

    return (
      this.isLowConfidence(course.course_code) ||
      this.isLowConfidence(course.course_name) ||
      this.isLowConfidence(course.semester) ||
      this.isLowConfidence(course.credits_attempted) ||
      this.isLowConfidence(course.credits_earned) ||
      this.isLowConfidence(course.grade)
    );
  }

  areObjectsEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  areAllTabsLowConfidenceFree(): boolean {
    return this.hasLowInTab.every((value) => value === false);
  }

  onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replaceAll(/[^a-zA-Z.\-\s]/g, "");
  }

  onInstitutionNameInput(event: Event, control: AbstractControl | null): void {
    if (!control) {
      return;
    }

    const input = event.target as HTMLInputElement;

    // remove all digits from the input
    const sanitized = input.value.replaceAll(/\d/g, "");

    // update the DOM value if it changed
    if (
      ComparisonNormalizer.string(sanitized) !==
      ComparisonNormalizer.string(input.value)
    ) {
      input.value = sanitized;
    }

    // update the reactive form control value
    control.setValue(sanitized, { emitEvent: true });
  }

  onTextInput(event: Event, model: any, field: string) {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = input.value.replaceAll(/\d/g, "");

    input.value = sanitizedValue;

    if (model && field) {
      model[field] = sanitizedValue;
    }
  }

  validateCreditsEarned(course: any): void {
    const earned = Number(course.credits_earned);
    const attempted = Number(course.credits_attempted);

    if (earned > attempted) {
      course.credits_earned = attempted;
    }
  }

  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 5000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  getSaveProceedTooltip(): string {
    if (this.isExtractionInProgress()) {
      return "Extraction is still in progress. Please wait for completion before proceeding.";
    }
    if (!this.isAnyCollegeAccredited()) {
      return "We couldn't verify the accreditation status of this college. Please check and update the status to 'Accredited' to continue with the evaluation process.";
    }
    if (this.areAllCreditSystemsEmpty()) {
      return "Credit Type is required. Please update it to continue the evaluation process";
    }
    if (!this.isAnyCollegeHasName()) {
      return "College Name is required. Please update it to continue the evaluation process";
    }
    if (this.hasDuplicateOrOlderTranscriptVersion()) {
      return "All transcripts are marked as duplicate or older versions. Please review transcript version to continue.";
    }
    if (this.isEditLocked()) {
      return "Kindly save the edit changes to proceed";
    }
    return "";
  }

  areAllCreditSystemsEmpty(): boolean {
    const transcripts = this.extractionResult?.transcripts;

    if (!transcripts || transcripts.length === 0) {
      // If there are no transcripts, treat as "all empty" → keep button disabled.
      return true;
    }

    // Return true only if every transcript has an empty/undefined credit_system
    return transcripts.every((_, i) => {
      const val = this.getCollegeForm(i)?.get("credit_system")?.value;
      return val === null || val === undefined || val === "";
    });
  }

  addNewCourse(): void {
    this.initNewCourseForm();
    this.isCourseAddVisible = !this.isCourseAddVisible;
    this.activeEdit = {
      section: "course",
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
  }

  cancelCourseCreation(): void {
    this.isCourseAddVisible = false;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.transcriptAlert = false;
  }

  saveCourseCreation(transcriptIndex: number): void {
    if (this.newCourseForm.invalid) {
      this.newCourseForm.markAllAsTouched();
      const message = this.getCourseValidationMessage(0, 0, "add");
      this.showToast("toast-error", message);
      return;
    }

    const formValue = this.newCourseForm.value;
    const newCourse = {
      college_code:
        this.extractionResult.transcripts[transcriptIndex].courses[0]
          ?.college_code || "",
      course_category: "",
      course_code: formValue.course_code,
      course_name: formValue.course_name,
      course_order:
        this.extractionResult.transcripts[transcriptIndex].courses.length + 1,
      credits_attempted: formValue.credits_attempted,
      credits_earned: formValue.credits_earned,
      grade: formValue.grade,
      semester: formValue.semester,
      total_grade_points: "0.0",
      transcript_status: "PR",
      source: "manual_entry",
    };

    // Add to data model and original snapshot
    this.extractionResult.transcripts[transcriptIndex].courses.push(newCourse);
    this.originalData.transcripts[transcriptIndex].courses.push(
      structuredClone(newCourse),
    );

    // Add new FormGroup to reactive course form array
    const newCourseGroup = this.fb.group(
      {
        course_code: [
          newCourse.course_code,
          [Validators.required, Validators.maxLength(50)],
        ],
        course_name: [
          newCourse.course_name,
          [Validators.required, Validators.maxLength(555)],
        ],
        semester: [
          newCourse.semester,
          [Validators.required, Validators.maxLength(50)],
        ],
        credits_attempted: [
          newCourse.credits_attempted,
          [Validators.required, Validators.min(0), Validators.max(10)],
        ],
        credits_earned: [
          newCourse.credits_earned,
          [Validators.required, Validators.min(0), Validators.max(10)],
        ],
        grade: [
          newCourse.grade,
          [Validators.required, Validators.maxLength(10)],
        ],
      },
      { validators: this.creditsEarnedNotExceedAttemptedValidator },
    );
    this.getCourseFormArray(transcriptIndex).push(newCourseGroup);

    // Keep initialCourseValue in sync so cancel-edit works for the new course
    if (Array.isArray(this.initialCourseValue)) {
      if (!this.initialCourseValue[transcriptIndex]) {
        this.initialCourseValue[transcriptIndex] = [];
      }
      this.initialCourseValue[transcriptIndex].push(
        newCourseGroup.getRawValue(),
      );
    }

    // New courses are manually entered — set full confidence
    if (!this.confidenceResult.transcripts[transcriptIndex].courses) {
      this.confidenceResult.transcripts[transcriptIndex].courses = [];
    }
    this.confidenceResult.transcripts[transcriptIndex].courses.push({
      course_code: 1,
      course_name: 1,
      course_category: 1,
      semester: 1,
      credits_attempted: 1,
      credits_earned: 1,
      grade: 1,
    });

    this.newCourseForm.reset();
    this.isCourseAddVisible = false;
    this.activeEdit = {
      section: null,
      transcriptIndex: null,
      courseIndex: null,
    };
    this.emitEditLockState();
    this.disableTabs.emit(this.currentStage.toLowerCase());
    this.showToast(
      "toast-success",
      "New course added successfully. You can find it at the bottom of the course list.",
    );
    this.transcriptAlert = false;
  }

  deleteCourse(transcriptIndex: number, courseIndex: number): void {
    const dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
      width: "400px",
      data: {
        message: "Are you sure you want to delete this course?",
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (ComparisonNormalizer.string(result) !== "yes") {
          return;
        }

        this.removeCourseFromModels(transcriptIndex, courseIndex);
        this.removeCourseFromFormArray(transcriptIndex, courseIndex);
        this.removeCourseFromInitialValues(transcriptIndex, courseIndex);
        this.removeCourseFromConfidence(transcriptIndex, courseIndex);
        this.rekeySavedCourseChanges(transcriptIndex, courseIndex);

        this.updateHasLowInTab();
        this.disableTabs.emit(this.currentStage.toLowerCase());
        this.showToast("toast-success", "Course deleted successfully.");
      });
  }

  private removeCourseFromModels(
    transcriptIndex: number,
    courseIndex: number,
  ): void {
    this.extractionResult.transcripts[transcriptIndex].courses.splice(
      courseIndex,
      1,
    );
    this.originalData.transcripts[transcriptIndex].courses.splice(
      courseIndex,
      1,
    );
  }

  private removeCourseFromFormArray(
    transcriptIndex: number,
    courseIndex: number,
  ): void {
    const courseArray = this.getCourseFormArray(transcriptIndex);
    if (!courseArray || courseArray.length <= courseIndex) {
      return;
    }

    courseArray.removeAt(courseIndex);
  }

  private removeCourseFromInitialValues(
    transcriptIndex: number,
    courseIndex: number,
  ): void {
    if (
      !Array.isArray(this.initialCourseValue) ||
      !Array.isArray(this.initialCourseValue[transcriptIndex])
    ) {
      return;
    }

    this.initialCourseValue[transcriptIndex].splice(courseIndex, 1);
  }

  private removeCourseFromConfidence(
    transcriptIndex: number,
    courseIndex: number,
  ): void {
    if (
      !Array.isArray(
        this.confidenceResult?.transcripts?.[transcriptIndex]?.courses,
      )
    ) {
      return;
    }

    this.confidenceResult.transcripts[transcriptIndex].courses.splice(
      courseIndex,
      1,
    );
  }

  private rekeySavedCourseChanges(
    transcriptIndex: number,
    courseIndex: number,
  ): void {
    const transcriptCourseChanges = this.savedChanges.course?.[transcriptIndex];
    if (!transcriptCourseChanges) {
      return;
    }

    const reKeyed: { [idx: number]: any } = {};
    for (const key of Object.keys(transcriptCourseChanges).map(Number)) {
      if (key < courseIndex) {
        reKeyed[key] = transcriptCourseChanges[key];
        continue;
      }

      if (key > courseIndex) {
        reKeyed[key - 1] = transcriptCourseChanges[key];
      }
    }

    this.savedChanges.course[transcriptIndex] = reKeyed;
  }

  isAnyCollegeHasName(): boolean {
    let value = this.extractionResult.transcripts.some(
      (t) => ComparisonNormalizer.string(t.college?.institution_name) !== "",
    );
    return value;
  }

  private getTranscriptValidationIssues(transcriptIndex: number): string[] {
    const transcript = this.extractionResult?.transcripts?.[transcriptIndex];
    if (!transcript) {
      return [];
    }

    const collegeForm = this.getCollegeForm(transcriptIndex);

    const accreditationValue =
      collegeForm?.get("is_accredited")?.value ??
      transcript?.college?.college_accreditation?.is_accredited;
    const transcriptVersion =
      collegeForm?.get("transcript_version")?.value ??
      transcript?.college?.transcript_version;
    const creditTypeValue =
      collegeForm?.get("credit_system")?.value ??
      transcript?.college?.credit_system;
    const collegeNameValue =
      collegeForm?.get("institution_name")?.value ??
      transcript?.college?.institution_name;

    const issues: string[] = [];

    if (ComparisonNormalizer.boolean(accreditationValue) === false) {
      issues.push("not accredited");
    }

    if (ComparisonNormalizer.string(transcriptVersion) === "duplicate") {
      issues.push("duplicate");
    }

    if (ComparisonNormalizer.string(transcriptVersion) === "older") {
      issues.push("older");
    }

    if (ComparisonNormalizer.string(creditTypeValue) === "") {
      issues.push("without credit type");
    }

    if (ComparisonNormalizer.string(collegeNameValue) === "") {
      issues.push("without college name");
    }

    return issues;
  }

  getTranscriptValidationMessage(transcriptIndex: number): string {
    const issues = this.getTranscriptValidationIssues(transcriptIndex);

    if (issues.length === 0) {
      return "";
    }

    return `This transcript has been identified as ${issues.join(", ")}. Hence this transcript will not proceed to the Evaluation stage.`;
  }

  hasDuplicateOrOlderTranscriptVersion(): boolean {
    const transcripts = this.extractionResult?.transcripts;

    if (!Array.isArray(transcripts) || transcripts.length === 0) {
      return false;
    }

    return transcripts.every((transcript, i) => {
      const formValue =
        this.getCollegeForm(i)?.get("transcript_version")?.value;
      const transcriptVersion =
        formValue ?? transcript?.college?.transcript_version;

      return transcriptVersion === "duplicate" || transcriptVersion === "older";
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
